import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { salesAPI } from '../services/api';
import { SalesMedia, SalesMediaKind } from '../types';
import { showCustomAlert } from './CustomAlert';

export const MEDIA_GROUPS: Array<{ kind: SalesMediaKind; label: string; addLabel: string }> = [
  { kind: 'product', label: 'Slike proizvoda', addLabel: 'Dodaj sliku proizvoda' },
  { kind: 'section', label: 'Preseci', addLabel: 'Dodaj presek' },
  { kind: 'pictogram', label: 'Piktogrami', addLabel: 'Dodaj piktogram' }
];

// Slike traže prijavu, pa se dohvataju kroz API; ista sličica se ne preuzima dvaput
const thumbCache = new Map<string, Promise<string>>();
const thumbUrl = (id: string) => {
  let url = thumbCache.get(id);
  if (!url) {
    url = salesAPI.getMediaThumbnail(id).then((blob) => URL.createObjectURL(blob));
    url.catch(() => thumbCache.delete(id));
    thumbCache.set(id, url);
  }
  return url;
};

export const SalesMediaImage: React.FC<{ id: string; alt: string; size: number }> = ({ id, alt, size }) => {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let active = true;
    setSrc('');
    thumbUrl(id)
      .then((url) => active && setSrc(url))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [id]);
  const box: React.CSSProperties = { width: size, height: size, display: 'block', objectFit: 'contain', background: '#fff' };
  return src ? <img src={src} alt={alt} style={box} /> : <div style={box} aria-hidden="true" />;
};

const fileBaseName = (file: File) => file.name.replace(/\.[^.]+$/, '').trim();

export const MediaUploadButton: React.FC<{
  kind: SalesMediaKind;
  label: string;
  onUploaded: (media: SalesMedia) => void;
  onError: (message: string) => void;
}> = ({ kind, label, onUploaded, onError }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          setBusy(true);
          try {
            onUploaded(await salesAPI.uploadMedia(kind, fileBaseName(file), file));
          } catch (err: any) {
            onError(err?.response?.data?.message || 'Slika nije otpremljena');
          } finally {
            setBusy(false);
          }
        }}
      />
      <Button type="button" label={label} icon="pi pi-upload" size="small" outlined loading={busy} onClick={() => inputRef.current?.click()} />
    </>
  );
};

// Izbor iz biblioteke; "multiple" čuva redosled klikova, jer je to redosled piktograma u PDF-u
export const MediaPicker: React.FC<{
  items: SalesMedia[];
  selected: string[];
  multiple?: boolean;
  thumbSize?: number;
  onChange: (ids: string[]) => void;
}> = ({ items, selected, multiple, thumbSize = 96, onChange }) => (
  <div className="flex flex-wrap gap-2" style={{ maxHeight: 290, overflowY: 'auto' }}>
    {items.map((m) => {
      const position = selected.indexOf(m._id);
      const isSelected = position >= 0;
      return (
        <button
          key={m._id}
          type="button"
          aria-pressed={isSelected}
          onClick={() => {
            if (multiple) onChange(isSelected ? selected.filter((id) => id !== m._id) : [...selected, m._id]);
            else onChange(isSelected ? [] : [m._id]);
          }}
          style={{ position: 'relative', width: thumbSize + 16, padding: 6, background: '#fff', borderRadius: 8, cursor: 'pointer', border: `2px solid ${isSelected ? '#2563eb' : '#e2e8f0'}` }}
        >
          <SalesMediaImage id={m._id} alt={m.name} size={thumbSize} />
          <div style={{ fontSize: 12, marginTop: 4, overflowWrap: 'anywhere' }}>{m.name}</div>
          {isSelected && multiple && (
            <span style={{ position: 'absolute', top: 4, right: 4, background: '#2563eb', color: '#fff', borderRadius: 10, minWidth: 20, fontSize: 12, lineHeight: '20px' }}>{position + 1}</span>
          )}
        </button>
      );
    })}
    {!items.length && <span style={{ color: '#94a3b8' }}>Biblioteka je prazna.</span>}
  </div>
);

const SalesMediaLibrary: React.FC<{
  media: SalesMedia[];
  onChanged: () => void;
  onError: (message: string) => void;
}> = ({ media, onChanged, onError }) => {
  const rename = async (item: SalesMedia, name: string) => {
    const trimmed = name.trim();
    if (trimmed === item.name) return;
    try {
      await salesAPI.renameMedia(item._id, trimmed);
    } catch (e: any) {
      onError(e?.response?.data?.message || 'Naziv nije sačuvan');
    }
    // I posle greške se ponovo učitava, da polje vrati važeći naziv
    onChanged();
  };

  const remove = (item: SalesMedia) => {
    showCustomAlert.confirm(
      `Obrisati "${item.name}" iz biblioteke?`,
      async () => {
        try {
          await salesAPI.deleteMedia(item._id);
          onChanged();
        } catch (e: any) {
          onError(e?.response?.data?.message || 'Slika nije obrisana');
        }
      },
      'Potvrda brisanja'
    );
  };

  return (
    <div>
      <div style={{ color: '#64748b', marginBottom: '1rem' }}>Dodate slike se automatski kompresuju. Slika koju koristi neki proizvod ne može da se obriše.</div>
      {MEDIA_GROUPS.map((group) => {
        const items = media.filter((m) => m.kind === group.kind);
        return (
          <section key={group.kind} style={{ marginBottom: '1.75rem' }}>
            <div className="flex flex-wrap align-items-center justify-content-between gap-2" style={{ marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0 }}>
                {group.label} ({items.length})
              </h3>
              <MediaUploadButton kind={group.kind} label={group.addLabel} onUploaded={onChanged} onError={onError} />
            </div>
            <div className="flex flex-wrap gap-3">
              {items.map((item) => (
                <div key={item._id} style={{ width: 170, border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, background: '#fff' }}>
                  <div className="flex justify-content-center">
                    <SalesMediaImage id={item._id} alt={item.name} size={group.kind === 'pictogram' ? 90 : 150} />
                  </div>
                  <InputText
                    key={item.name}
                    defaultValue={item.name}
                    aria-label="Naziv slike"
                    onBlur={(e) => void rename(item, e.target.value)}
                    style={{ width: '100%', marginTop: 8, padding: '0.35rem 0.5rem', fontSize: 13 }}
                  />
                  <div className="flex align-items-center justify-content-between" style={{ marginTop: 4 }}>
                    <small style={{ color: '#94a3b8' }}>{Math.max(1, Math.round((item.size || 0) / 1024))} KB</small>
                    <Button type="button" icon="pi pi-trash" text rounded size="small" severity="danger" aria-label={`Obriši ${item.name}`} onClick={() => remove(item)} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default SalesMediaLibrary;
